import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  AddRounded,
  AdjustRounded,
  AttachFileOutlined,
  BoltRounded,
  CancelOutlined,
  ChatBubbleOutlineRounded,
  ChecklistRtlOutlined,
  CheckRounded,
  ContentCopyRounded,
  DeleteOutlineRounded,
  EmailOutlined,
  EditOutlined,
  EventOutlined,
  FileUploadOutlined,
  GppMaybeOutlined,
  InfoOutlined,
  KeyboardArrowDownRounded,
  LinkOutlined,
  RadioButtonUncheckedRounded,
  SearchRounded,
  SyncOutlined,
  VisibilityOutlined,
  KeyboardArrowRightOutlined,
  TableChartOutlined,
  ChecklistOutlined,
  EastOutlined,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputBase,
  Menu,
  MenuItem,
  Tooltip,
} from "@mui/material";
import { isArray, isEmpty, isNil, size } from "lodash";

import HtmlEditor from "../../../../components/html/HtmlEditor";
import Flx from "../../../../components/layout/Flx";
import LoanConditionCommentInput from "../../../../components/comments/LoanConditionCommentInput";
import CommentsRenderer from "../../../../components/renderers/CommentsRenderer";
import DocumentIdsRenderer from "../../../../components/renderers/DocumentIdsRenderer";
import SelectFileAssignmentInput from "../../../../components/inputs/SelectFileAssignmentInput";
import Txt from "../../../../components/typography/Txt";
import {
  DOC_STIP_STATUS_OPTIONS,
  STATUS_OPTIONS,
} from "../../../../components/forms/CreateUpdateLoanConditionForm";
import FileUploadProcess from "../../../../components/processes/FileUploadProcess";
import AssociationsDisplay from "../../../../components/associations/AssociationsDisplay";
import AssociationsEditor from "../../../../components/associations/AssociationsEditor";
import { loanDrilldownSidebarTypes } from "../../../../constants/enums/loanDrilldownScreenSidebarOptions";
import {
  CONDITION_GATE,
  CONDITION_GATE_VALUES,
  getConditionGateLabel,
  getConditionGateShortLabel,
} from "../../../../constants/selectOptions/selectOptionsConditionGate";
import YesNoSwitch from "../../../../components/inputs/YesNoSwitch";
import { useChecklistRouteHook } from "../../../../hooks/useChecklistRouteHook";
import { useUnderwritingHook } from "../../../../hooks/useUnderwritingHook";
import { usePermissionsHook } from "../../../../hooks/usePermissionsHook";
import {
  sidebarSetStateValue,
  sidebarTypeSet,
} from "../../../../redux/actions/sidebarActions";
import { loanDrilldownSet } from "../../../../redux/actions/loanDrilldownActions";
import { loanFetchedDataUwRequirementsSet } from "../../../../redux/reducers/loanFetchedDataReducer";
import { htmlStringToText } from "../../../../_src_shared/utils/functions/htmlStringToText";
import { conditionTitle } from "../../../../utils/conditions/conditionTitle";
import { formatDate } from "../../../../_src_shared/utils/functions/formatDate";
import { formatDateTime } from "../../../../_src_shared/utils/functions/formatDateTime";
import { getUserFullNameFromEmail } from "../../../../utils/functions/getUserFullNameFromEmail";
import { partnerStorage } from "../../../../utils/storage/partnerStorage";
import LoanDrilldownConditionsTab from "../LoanDrilldownConditionsTab";
import AiFindingsPanel from "../../components/AiFindingsPanel";
import {
  AI_SUGGESTION_SCOPES,
  useAiSuggestionIgnored,
} from "../../../../utils/storage/aiSuggestionVisibility";
import { grey } from "@mui/material/colors";
import FlxGrid from "../../../../_src_shared/components/layout/FlxGrid";

/* ----------------------------------- tokens -------------------------------- */

const ACCENT = "#0e7490"; // cyan-700
const ACCENT_BAR = "#0891b2"; // cyan-600 — left bar on the open row
const VIEW_STORAGE_KEY = "conditionsTabView"; // partner-scoped via partnerStorage

// Slate scale used throughout (matches Tailwind slate)
const S = {
  50: "#f8fafc",
  100: "#f1f5f9",
  200: "#e2e8f0",
  300: "#cbd5e1",
  400: "#94a3b8",
  500: "#64748b",
  600: "#475569",
  700: "#334155",
  800: "#1e293b",
  900: "#0f172a",
};

// One-time keyframes for the strike-through + detail pop (same as the prototype).
const GLOBAL_CSS = `
.ckStrike { position: relative; color: ${S[400]}; }
.ckStrike::after {
  content: ''; position: absolute; left: 0; top: 55%; height: 1.5px;
  background: ${S[400]}; width: 100%; transform-origin: left;
  animation: ckStrike .3s ease;
}
@keyframes ckStrike { from { transform: scaleX(0); } to { transform: scaleX(1); } }
.ckPop { animation: ckPop .18s ease; }
@keyframes ckPop { from { opacity: 0; transform: translateY(-3px); } to { opacity: 1; transform: none; } }
@media (prefers-reduced-motion: reduce) { .ckStrike::after, .ckPop { animation: none; } }
`;

// Compact native-select styling used for the inline doc-status picker.
const SELECT_SX = {
  font: "inherit",
  fontSize: 12,
  fontWeight: 500,
  color: S[700],
  border: `1px solid ${S[200]}`,
  borderRadius: "6px",
  background: "#fff",
  px: 0.75,
  py: 0.4,
  cursor: "pointer",
  "&:focus": { outline: "none", borderColor: ACCENT },
};

// Uppercase micro-label that titles each card section in the expanded view.
const SECTION_LABEL_SX = {
  display: "flex",
  alignItems: "center",
  gap: 0.75,
  mb: 1.5,
  fontSize: 10,
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  color: S[400],
};
const SUB_SECTION_LABEL_SX = {
  display: "flex",
  alignItems: "center",
  gap: 0.75,
  mb: 1,
  fontSize: 10,
  fontWeight: 400,
  letterSpacing: "0.05em",
  color: S[700],
};

// White card on the gray tray — the core surface of the expanded view.
const CARD_SX = {
  borderRadius: "8px",
  border: `1px solid ${S[200]}`,
  background: "#fff",
  px: 2,
  py: 1.5,
};

/* --------------- reusable master-detail card + section shells -------------- */

// A white card surface. Pass `sx` to tweak padding / color per use.
const DetailCard = ({ sx, children }) => (
  <Box component="section" sx={{ ...CARD_SX, ...sx }}>
    {children}
  </Box>
);

// A titled block inside a card: an uppercase label (optional leading `icon` and
// trailing `count`), an optional right-aligned `endContent`, then `children`.
// `labelSx` tweaks the label (e.g. spacing/color) for a given use.
const DetailSection = ({
  title,
  icon,
  count,
  endContent,
  labelSx,
  children,
  sx,
}) => {
  const label = (
    <Box sx={{ ...SECTION_LABEL_SX, mb: endContent ? 0 : 1.5, ...labelSx }}>
      {icon}
      {title}
      {count > 0 && (
        <Box
          component="span"
          sx={{
            fontWeight: 600,
            textTransform: "none",
            letterSpacing: 0,
            color: S[300],
          }}
        >
          · {count}
        </Box>
      )}
    </Box>
  );
  return (
    <Flx column sx={sx}>
      {endContent ? (
        <Flx ac jb sx={{ mb: 1.5 }}>
          {label}
          {endContent}
        </Flx>
      ) : (
        label
      )}
      {children}
    </Flx>
  );
};

// A single labelled control. Same label treatment as DetailSection, but built
// to sit beside a peer in a row so labels line up and values line up.
const LabeledField = ({ label, sx, children }) => (
  <Flx column g={1.5} sx={sx}>
    <Box sx={{ ...SECTION_LABEL_SX, mb: 0 }}>{label}</Box>
    {children}
  </Flx>
);

const VIEWS = [
  { value: "classic", label: "Table", icon: <TableChartOutlined /> },
  { value: "checklist", label: "Checklist", icon: <ChecklistOutlined /> },
];

/* --------------------------- old / new view toggle ------------------------- */

const ViewToggle = ({ value, onChange }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      background: "#eef3f5",
      border: "1px solid #e3e9ec",
      borderRadius: "9px",
      p: "3px",
    }}
  >
    {VIEWS.map(({ value: v, label, icon }) => {
      const on = value === v;
      return (
        <Box
          key={v}
          component="button"
          type="button"
          onClick={() => onChange(v)}
          sx={{
            font: "inherit",
            fontSize: 12.5,
            fontWeight: on ? 600 : 500,
            color: on ? S[900] : S[500],
            border: "none",
            background: on ? "#fff" : "transparent",
            boxShadow: on ? "0 1px 3px rgba(22,36,43,.12)" : "none",
            px: "13px",
            py: "6px",
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            borderRadius: "7px",
            cursor: "pointer",
            svg: {
              fontSize: 16,
            },
          }}
        >
          {icon}
          {label}
        </Box>
      );
    })}
  </Box>
);

// Status chip click cycles through these (Accepted/Rejected live elsewhere)
const CYCLE = ["Open", "In Progress", "Second Level Review"];

// `bg` is the soft tint used by the pill selectors; `chipBg` is a slightly
// deeper tint used by the compact status/gate chips in the row so they read
// against the white row background.
const STATUS = {
  Open: {
    label: "Open",
    fg: "#64748b",
    bg: "#f1f5f9",
    chipBg: "#e2e8f0",
    Icon: RadioButtonUncheckedRounded,
  },
  "In Progress": {
    label: "In Progress",
    fg: "#b45309",
    bg: "#fffbeb",
    chipBg: "#fef3c7",
    Icon: AdjustRounded,
  },
  "Second Level Review": {
    label: "2nd Review",
    fg: "#1d4ed8",
    bg: "#eff6ff",
    chipBg: "#dbeafe",
    Icon: GppMaybeOutlined,
  },
  Accepted: {
    label: "Accepted",
    fg: "#0f766e",
    bg: "#f0fdfa",
    chipBg: "#ccfbf1",
    Icon: CheckRounded,
  },
  Rejected: {
    label: "Rejected",
    fg: "#b91c1c",
    bg: "#fee2e2",
    chipBg: "#fecaca",
    Icon: CancelOutlined,
  },
};

// Gate toggle in the master detail (mirrors the form's "Type" field). The old
// Condition/Document/Stip requirement-type pills are gone: requirementType is
// now driven by the "Requires Document" switch below.
const CONDITION_GATE_META = {
  [CONDITION_GATE.PRIOR_TO_DOCS]: {
    label: "Prior to Docs",
    fg: "#1d4ed8",
    bg: "#eff6ff",
    chipBg: "#dbeafe",
  },
  [CONDITION_GATE.PRIOR_TO_FUNDING]: {
    label: "Prior to Funding",
    fg: "#b45309",
    bg: "#fffbeb",
    chipBg: "#fef3c7",
  },
  [CONDITION_GATE.POST_CLOSE]: {
    label: "Post Close",
    fg: "#0f766e",
    bg: "#f0fdfa",
    chipBg: "#ccfbf1",
  },
};

// Colors for the parallel document lifecycle (docStipStatus). Mirrors the
// STATUS palette so the doc-status pill selector reads the same as the
// condition-status one — neutral when open, amber in review, red when bounced,
// green when accepted.
const DOC_STATUS = {
  "Open Item": {
    label: "Open Item",
    fg: S[500],
    bg: S[100],
    Icon: RadioButtonUncheckedRounded,
  },
  "Received - Pending Review": {
    label: "Pending Review",
    fg: "#92400e",
    bg: "#fef3c7",
    Icon: AdjustRounded,
  },
  "Reviewed - Not Accepted": {
    label: "Not Accepted",
    fg: "#b91c1c",
    bg: "#fee2e2",
    Icon: CancelOutlined,
  },
  Accepted: {
    label: "Accepted",
    fg: "#047857",
    bg: "#d1fae5",
    Icon: CheckRounded,
  },
};

// The checklist groups conditions by their real `requirementType` field.
const GROUP_ORDER = ["Condition", "Stip", "Document"];
const groupLabel = (type) => {
  if (type === "Document") return "Required Documents";
  if (type === "Stip") return "Stipulations";
  if (type === "Condition") return "Conditions";
  return type || "Other";
};
const typeOf = (c) => c?.requirementType || "Other";

/* --------------------------------- helpers --------------------------------- */


// A condition's documents are the union of `uploadedDocumentIds` (uploaded
// against it) and `associations.loanDocuments` (linked / uploaded elsewhere and
// attached) — the same rule the Documents tab uses.
const conditionDocIds = (c) => [
  ...new Set([
    ...(Array.isArray(c?.uploadedDocumentIds) ? c.uploadedDocumentIds : []),
    ...(Array.isArray(c?.associations?.loanDocuments)
      ? c.associations.loanDocuments
      : []),
  ]),
];

const isAccepted = (c) => c?.status === "Accepted";

// Segmented progress cell background (one cell per condition) — mirrors the
// reference: the status' dot color at ~33% opacity, light grey for Open, solid
// teal for Accepted.
const PROGRESS_DOT = {
  Open: "#94a3b8",
  "In Progress": "#f59e0b",
  "Second Level Review": "#3b82f6",
  Accepted: "#14b8a6",
  Rejected: "#ef4444",
};
const progressCellColor = (status) => {
  if (status === "Accepted") return "#0f766e";
  const dot = PROGRESS_DOT[status] || PROGRESS_DOT.Open;
  return dot === "#94a3b8" ? "#e2e8f0" : `${dot}55`;
};

/* ---------------------- copy conditions as email text ---------------------- */

const escHtml = (s) =>
  String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

// Default statuses pre-checked for the "By status" email scope (a missing
// status defaults to Open on the model, so Open covers untagged conditions).
const COPY_STATUSES = ["Open", "In Progress"];

// Section header used in the email — the "Condition" group reads as "Missing Items".
const emailGroupLabel = (type) =>
  type === "Condition" ? "Missing Items" : groupLabel(type);

// In the email, missing-document conditions are listed alongside the other
// missing items (no separate "Required Documents" section) and read as the
// bare doc type.
const emailTypeOf = (c) =>
  typeOf(c) === "Document" ? "Condition" : typeOf(c);
const emailConditionTitle = (c) => c?.docType || conditionTitle(c);

// Group the given conditions by requirementType (GROUP_ORDER first) into
// { label, titles[] } sections, skipping empty groups. The caller decides which
// items to pass (needs-action vs. what's currently displayed).
const groupConditionsForEmail = (items) => {
  const present = [];
  items.forEach((i) => {
    const type = emailTypeOf(i);
    if (!present.includes(type)) present.push(type);
  });
  const ordered = [
    ...GROUP_ORDER.filter((t) => present.includes(t)),
    ...present.filter((t) => !GROUP_ORDER.includes(t)),
  ];
  return ordered
    .map((type) => ({
      label: emailGroupLabel(type),
      titles: items
        .filter((i) => emailTypeOf(i) === type)
        .map(emailConditionTitle),
    }))
    .filter((g) => g.titles.length > 0);
};

// Build both an HTML and a plain-text representation of the conditions list,
// structured as titled, numbered sections — ready to paste into an email client.
const buildConditionsEmail = (items, loan) => {
  const sections = groupConditionsForEmail(items);
  const subject = loan?.loanName || loan?.loanNumber || "Loan Conditions";

  const htmlParts = [];
  htmlParts.push(`<p style="margin:0 0 8px">Hi Team,</p>`);
  htmlParts.push(
    `<p style="margin:0 0 4px">Please see updated conditions list below:</p>`,
  );
  sections.forEach((s) => {
    htmlParts.push(
      `<p style="margin:12px 0 4px"><u><b>${escHtml(s.label)}:</b></u></p>`,
    );
    htmlParts.push(
      `<ol style="margin:0 0 8px 0;padding-left:24px">${s.titles
        .map((t) => `<li style="margin:2px 0">${escHtml(t)}</li>`)
        .join("")}</ol>`,
    );
  });
  const html = `<div style="font-family:Arial,Helvetica,sans-serif;font-size:14px;color:#1e293b">${htmlParts.join(
    "",
  )}</div>`;

  const textParts = [
    "Hi Team,",
    "",
    "Please see updated conditions list below:",
    "",
  ];
  sections.forEach((s) => {
    textParts.push(`${s.label}:`);
    s.titles.forEach((t, i) => textParts.push(`  ${i + 1}. ${t}`));
    textParts.push("");
  });
  const text = textParts.join("\n").trim();

  return { html, text, subject };
};

// Both openers send the loan name as the subject and the conditions list as the
// body. The body is plain-text only in both cases (neither a mailto nor the OWA
// deeplink can carry HTML) — for a formatted list, use "Copy for Email" + paste.

// Outlook on the web compose tab (M365 / work accounts).
const OUTLOOK_WEB_COMPOSE = "https://outlook.office.com/mail/deeplink/compose";
const openOutlookWebEmail = ({ subject, text }) => {
  const url = `${OUTLOOK_WEB_COMPOSE}?subject=${encodeURIComponent(
    subject,
  )}&body=${encodeURIComponent(text)}`;
  window.open(url, "_blank", "noopener,noreferrer");
};

// The OS default mail client (Outlook desktop, Mail, etc.) via mailto:.
const openDefaultEmailClient = ({ subject, text }) => {
  const url = `mailto:?subject=${encodeURIComponent(
    subject,
  )}&body=${encodeURIComponent(text)}`;
  window.open(url, "_self");
};

// Write rich HTML + plain text to the clipboard (so pasting into an email
// client keeps the formatting); falls back to plain text where unsupported.
const copyEmailToClipboard = async ({ html, text }) => {
  try {
    if (navigator.clipboard && window.ClipboardItem) {
      await navigator.clipboard.write([
        new window.ClipboardItem({
          "text/html": new Blob([html], { type: "text/html" }),
          "text/plain": new Blob([text], { type: "text/plain" }),
        }),
      ]);
    } else {
      await navigator.clipboard.writeText(text);
    }
    return true;
  } catch {
    return false;
  }
};

const dueLabel = (dateStr, done) => {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(d);
  target.setHours(0, 0, 0, 0);
  const days = Math.round((target - today) / 86400000);
  const label = formatDate(target, "", "MMM d");
  if (done) return { label, color: S[300], bold: false };
  if (days < 0)
    return { label: `${-days}d overdue`, color: "#dc2626", bold: true };
  if (days <= 3) return { label, color: "#b45309", bold: true };
  return { label, color: S[400], bold: false };
};

/* ------------------------------- status chip ------------------------------- */

const StatusChip = ({ status, onClick, loading }) => {
  const s = STATUS[status] || STATUS.Open;
  const Icon = s.Icon;
  return (
    <Box
      // component="button"
      // onClick={onClick}
      // disabled={loading}
      // title="Click to change status"
      sx={{
        flexShrink: 0,
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        border: "none",
        cursor: loading ? "default" : "pointer",
        borderRadius: 999,
        px: 1,
        py: 0.3,
        fontSize: 11,
        fontWeight: 600,
        background: s.chipBg || s.bg,
        color: s.fg,
        opacity: loading ? 0.6 : 1,
        transition: "filter 0.15s, opacity 0.15s",
        // "&:hover": { filter: loading ? "none" : "brightness(0.95)" },
      }}
    >
      {loading ? (
        <CircularProgress size={10} sx={{ color: s.fg }} />
      ) : (
        <Icon sx={{ fontSize: 12 }} />
      )}
      {s.label}
    </Box>
  );
};

/* --------------------------------- gate chip -------------------------------- */

// Compact conditionGate (Type) badge for the collapsed row. Same pill shape as
// StatusChip; colors come from CONDITION_GATE_META. Renders nothing when the
// condition has no gate set.
const GateChip = ({ gate }) => {
  if (!gate) return null;
  const meta = CONDITION_GATE_META[gate];
  const fg = meta ? meta.fg : S[500];
  const bg = meta ? meta.chipBg || meta.bg : S[200];
  return (
    <Tooltip title={`Type: ${getConditionGateLabel(gate)}`} placement="top">
      <Box
        sx={{
          flexShrink: 0,
          display: "inline-flex",
          alignItems: "center",
          border: "none",
          borderRadius: 999,
          px: 1,
          py: 0.3,
          fontSize: 11,
          fontWeight: 600,
          background: bg,
          color: fg,
          whiteSpace: "nowrap",
        }}
      >
        {getConditionGateShortLabel(gate)}
      </Box>
    </Tooltip>
  );
};

/* --------------------------- status pill selector -------------------------- */

// Segmented pill selector for condition status. Options come from the existing
// edit-condition form (STATUS_OPTIONS); labels/colors from the shared STATUS map.
// `options`/`statusMeta`/`fallbackKey` are overridable so the same control can
// drive the parallel document-status lifecycle (docStipStatus).
const StatusPills = ({
  value,
  onChange,
  loading,
  options = STATUS_OPTIONS,
  statusMeta = STATUS,
  fallbackKey = "Open",
}) => (
  <Flx ac wrap g={0.75}>
    {options.map((s) => {
      const meta = statusMeta[s] || statusMeta[fallbackKey];
      const active = value === s;
      return (
        <Box
          key={s}
          component="button"
          type="button"
          disabled={loading}
          onClick={() => onChange(s)}
          sx={{
            font: "inherit",
            fontSize: 12,
            fontWeight: active ? 700 : 600,
            cursor: loading ? "default" : "pointer",
            borderRadius: 999,
            px: 1.5,
            py: 0.65,
            lineHeight: 1.2,
            // Active pills get a solid outline + soft ring in their own status
            // color so even low-contrast statuses (e.g. "Open", whose grey fill
            // sits close to the inactive tray) clearly read as selected.
            border: active ? `1.5px solid ${meta.fg}` : `1px solid ${S[200]}`,
            background: active ? meta.bg : S[50],
            color: active ? meta.fg : S[400],
            boxShadow: active
              ? `0 0 0 3px ${meta.fg}26, 0 1px 2px rgba(0,0,0,0.06)`
              : "none",
            opacity: loading && !active ? 0.55 : 1,
            transition: "all 0.12s",
            "&:hover":
              active || loading ? {} : { color: S[600], borderColor: S[300] },
          }}
        >
          {meta.label}
        </Box>
      );
    })}
  </Flx>
);

/* ----------------------- status filter toggle tabs ------------------------- */

// Segmented "All / <status>" tabs that filter the checklist by status. Only
// statuses present in the data are shown (plus "All"); each tab carries a count.
const StatusFilterTabs = ({ value, onChange, counts, total }) => {
  const needsCount = total - (counts.Accepted || 0);
  const tabs = [
    {
      key: "",
      label: "All Conditions",
      count: total,
      fg: "#dbdbdb",
      bg: "#dbdbdb",
    },
    {
      key: "needs",
      label: "Needs action",
      count: needsCount,
      fg: "#eab308",
      bg: "#eab308",
    },
    ...STATUS_OPTIONS.filter((s) => (counts[s] || 0) > 0).map((s) => ({
      key: s,
      label: STATUS[s]?.label || s,
      count: counts[s],
      fg: STATUS[s]?.fg,
      bg: STATUS[s]?.bg,
    })),
  ];
  return (
    <Flx ac wrap g={0.75}>
      <Txt
        sx={{
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: "0.04em",
          textTransform: "uppercase",
          color: S[400],
          mr: 0.25,
        }}
      >
        Status
      </Txt>
      {tabs.map((t) => {
        const active = value === t.key;
        return (
          <Box
            key={t.key || "all"}
            component="button"
            onClick={() => onChange(t.key)}
            sx={{
              font: "inherit",
              fontSize: 12,
              fontWeight: 570,
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              gap: 0.75,
              borderRadius: 999,
              // px: 1.25,
              // py: 0.5,
              p: "5px 12px",
              border: "1px solid transparent",
              background: active ? "#0f172a" : "transparent",
              color: active ? "#fff" : S[500],
              transition: "all 0.13s",
              "&:hover": active ? {} : { background: S[100] },
            }}
          >
            <Box
              component="span"
              sx={{
                width: 6,
                height: 6,
                borderRadius: 999,
                background: active ? t.bg : t.fg,
                flexShrink: 0,
              }}
            />
            {t.label}
            <Box
              component="span"
              sx={{
                fontSize: 11,
                fontWeight: 620,
                opacity: active ? 0.7 : 0.6,
              }}
            >
              {t.count}
            </Box>
          </Box>
        );
      })}
    </Flx>
  );
};

/* ------------------------ type (gate) filter toggle tabs ------------------- */

// Segmented "All Types / <gate>" tabs that filter the checklist by conditionGate.
// Mirrors StatusFilterTabs — only gates present in the data are shown (plus
// "All Types"), each with a count. Renders nothing when no condition has a gate.
const TypeFilterTabs = ({ value, onChange, counts, total }) => {
  const presentGates = CONDITION_GATE_VALUES.filter((g) => (counts[g] || 0) > 0);
  if (presentGates.length === 0) return null;
  const tabs = [
    {
      key: "",
      label: "All Types",
      count: total,
      // Neutral steel accent for the "all" chip so it reads distinctly from the
      // status row's near-black active pill.
      fg: "#2c5282",
      bg: "#e8eef5",
    },
    ...presentGates.map((g) => ({
      key: g,
      label: getConditionGateLabel(g),
      count: counts[g],
      fg: CONDITION_GATE_META[g]?.fg,
      bg: CONDITION_GATE_META[g]?.chipBg || CONDITION_GATE_META[g]?.bg,
    })),
  ];
  return (
    <Flx ac wrap g={0.75}>
      <Txt
        sx={{
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: "0.04em",
          textTransform: "uppercase",
          color: S[400],
          mr: 0.25,
        }}
      >
        Type
      </Txt>
      {tabs.map((t) => {
        const active = value === t.key;
        // Distinct from the status row: squared-off chips that fill with the
        // gate's own color when active (colored border + tint) instead of the
        // uniform dark pill the status tabs use.
        return (
          <Box
            key={t.key || "all"}
            component="button"
            onClick={() => onChange(t.key)}
            sx={{
              font: "inherit",
              fontSize: 12,
              fontWeight: active ? 650 : 550,
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              gap: 0.75,
              borderRadius: "7px",
              p: "4px 10px",
              border: `1px solid ${active ? t.fg : S[200]}`,
              background: active ? t.bg : "transparent",
              color: active ? t.fg : S[500],
              transition: "all 0.13s",
              "&:hover": active
                ? {}
                : { background: S[50], borderColor: S[300] },
            }}
          >
            <Box
              component="span"
              sx={{
                width: 6,
                height: 6,
                borderRadius: 999,
                background: t.fg,
                flexShrink: 0,
              }}
            />
            {t.label}
            <Box
              component="span"
              sx={{
                fontSize: 11,
                fontWeight: 700,
                opacity: active ? 0.85 : 0.55,
              }}
            >
              {t.count}
            </Box>
          </Box>
        );
      })}
    </Flx>
  );
};

/* ----------------------------------- tab ----------------------------------- */

// `embedded` tightens the layout for the drilldown sidebar (the conditions rail
// button renders this in a ~720px pane instead of full-page): drops the page
// max-width / large padding and hides the big "Conditions" title since the pane
// header already shows it.
const LoanDrilldownConditionsChecklistTab = ({ embedded = false }) => {
  const dispatch = useDispatch();
  const loan = useSelector((state) => state.loanDrilldown);
  const uwRequirements = useSelector(
    (state) => state.loanFetchedData?.uwRequirements,
  );
  const {
    loading,
    getLoanUnderwritingRequirements,
    updateLoanUnderwritingRequirement,
    createLoanUnderwritingRequirement,
    deleteLoanUnderwritingRequirement,
  } = useChecklistRouteHook();
  // Same gate as the AI Review tab (admin permission) — only these users see the
  // AI-suggested conditions section.
  const { isAdmin } = usePermissionsHook();
  // Per-user, cross-loan dismissal; AI Review → Suggested Conditions offers the
  // way back once it's set.
  const { ignored: aiCondIgnored, ignore: ignoreAiConditions } =
    useAiSuggestionIgnored(AI_SUGGESTION_SCOPES.CONDITIONS);

  const [query, setQuery] = useState("");
  // "" = all statuses · "needs" = outstanding (not Accepted) · else a status.
  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [openId, setOpenId] = useState(null);
  const [adding, setAdding] = useState(false); // inline add-row active?
  const [draftTitle, setDraftTitle] = useState("");
  // Per-item save state: { [conditionId]: true } while a mutation is in flight.
  const [pendingIds, setPendingIds] = useState({});
  // Ghost row shown while a newly created condition round-trips to the server.
  const [creating, setCreating] = useState(null); // { title } | null
  const [view, setView] = useState(
    () => partnerStorage.getItem(VIEW_STORAGE_KEY) || "checklist",
  );
  const [copied, setCopied] = useState(false);
  const [emailAnchor, setEmailAnchor] = useState(null);
  // Email scope: "byStatus" = conditions matching a chosen set of statuses
  // (default) · "displayed" = whatever rows are currently visible under the
  // active filter/search.
  const [emailScope, setEmailScope] = useState("byStatus");
  // Which statuses to include when emailScope === "byStatus".
  const [emailStatuses, setEmailStatuses] = useState(
    () => new Set(COPY_STATUSES),
  );
  const toggleEmailStatus = useCallback((s) => {
    setEmailStatuses((prev) => {
      const next = new Set(prev);
      if (next.has(s)) next.delete(s);
      else next.add(s);
      return next;
    });
  }, []);
  // Multi-select (bulk status change), mirroring the documents tab's bulk mode.
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkStatus, setBulkStatus] = useState("");
  const [bulkGate, setBulkGate] = useState("");
  const [bulkSaving, setBulkSaving] = useState(false);

  const onChangeView = useCallback((v) => {
    setView(v);
    partnerStorage.setItem(VIEW_STORAGE_KEY, v);
  }, []);

  const markPending = useCallback((id) => {
    setPendingIds((p) => ({ ...p, [id]: true }));
  }, []);
  const clearPending = useCallback((id) => {
    setPendingIds((p) => {
      const next = { ...p };
      delete next[id];
      return next;
    });
  }, []);

  useEffect(() => {
    if (
      isNil(uwRequirements) ||
      (!isArray(uwRequirements) && !isNil(loan?._id))
    ) {
      getLoanUnderwritingRequirements({
        dealId: loan?._id,
        onSuccessFn: (data) => dispatch(loanFetchedDataUwRequirementsSet(data)),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uwRequirements]);

  const items = useMemo(
    () => (isArray(uwRequirements) ? uwRequirements : []),
    [uwRequirements],
  );
  const done = items.filter(isAccepted).length;

  // Persist the conditions array returned by a mutation (array or {conditions}).
  const applyResponse = useCallback(
    (response) => {
      if (isArray(response)) {
        dispatch(loanFetchedDataUwRequirementsSet(response));
        return;
      }
      const next = response?.conditions || response?.comments;
      if (isArray(next)) dispatch(loanFetchedDataUwRequirementsSet(next));
    },
    [dispatch],
  );

  const patchCondition = useCallback(
    (condition, data) => {
      markPending(condition._id);
      updateLoanUnderwritingRequirement({
        dealId: loan?._id,
        requirementId: condition._id,
        data,
        onSuccessFn: (response) => {
          applyResponse(response);
          clearPending(condition._id);
        },
        // If the route hook exposes an error callback, clear pending there too so
        // a failed save doesn't leave the row spinning:
        onErrorFn: () => clearPending(condition._id),
      });
    },
    [
      loan?._id,
      updateLoanUnderwritingRequirement,
      applyResponse,
      markPending,
      clearPending,
    ],
  );

  const toggleDone = useCallback(
    (c) => {
      if (pendingIds[c._id]) return; // guard double-clicks while saving
      if (isAccepted(c)) {
        patchCondition(c, { status: "Open" });
      } else {
        const data = { status: "Accepted" };
        if (c.requirementType === "Document") data.docStipStatus = "Accepted";
        patchCondition(c, data);
      }
    },
    [patchCondition, pendingIds],
  );

  const cycleStatus = useCallback(
    (c) => {
      if (pendingIds[c._id]) return;
      if (c.status === "Accepted" || c.status === "Rejected") {
        return patchCondition(c, { status: "Open" });
      }
      const next = CYCLE[(CYCLE.indexOf(c.status) + 1) % CYCLE.length];
      patchCondition(c, { status: next });
    },
    [patchCondition, pendingIds],
  );

  const handleDelete = useCallback(
    (c) => {
      markPending(c._id);
      deleteLoanUnderwritingRequirement({
        dealId: loan?._id,
        requirementId: c._id,
        onSuccessFn: (response) => {
          if (response?.conditions) {
            dispatch(loanFetchedDataUwRequirementsSet(response.conditions));
          } else {
            applyResponse(response);
          }
          clearPending(c._id);
          setOpenId(null);
        },
        onErrorFn: () => clearPending(c._id),
      });
    },
    [
      loan?._id,
      deleteLoanUnderwritingRequirement,
      dispatch,
      applyResponse,
      markPending,
      clearPending,
    ],
  );

  const addItem = useCallback(() => {
    const title = draftTitle.trim();
    if (!title) {
      setAdding(false);
      return;
    }
    // Clear the input right away and show a ghost row until the server replies.
    setDraftTitle("");
    setCreating({ title });
    createLoanUnderwritingRequirement({
      dealId: loan?._id,
      data: {
        requirementType: "Condition",
        status: "Open",
        displayExternally: false,
        htmlString: `<p>${title}</p>`,
      },
      onSuccessFn: (response) => {
        applyResponse(response);
        setCreating(null);
      },
      onErrorFn: () => {
        // Restore the draft so the user's typing isn't lost on failure.
        setCreating(null);
        setAdding(true);
        setDraftTitle(title);
      },
    });
  }, [draftTitle, loan?._id, createLoanUnderwritingRequirement, applyResponse]);

  const onManage = useCallback(
    (c) => {
      dispatch(
        sidebarTypeSet(
          loanDrilldownSidebarTypes.ADD_EDIT_LOAN_CONDITION_REDESIGN,
        ),
      );
      dispatch(sidebarSetStateValue(c));
    },
    [dispatch],
  );

  // Opens the existing create-condition flow (full form: type, docType, etc.).
  const onAdd = useCallback(() => {
    dispatch(
      sidebarTypeSet(
        loanDrilldownSidebarTypes.ADD_EDIT_LOAN_CONDITION_REDESIGN,
      ),
    );
    dispatch(sidebarSetStateValue(null));
  }, [dispatch]);

  useEffect(() => {
    if (!copied) return undefined;
    const t = setTimeout(() => setCopied(false), 1600);
    return () => clearTimeout(t);
  }, [copied]);

  // Status change mirrors the existing form: accepting a Document also accepts its
  // doc-stip track (see CreateUpdateLoanConditionForm.handleOnUpdate).
  const setStatus = useCallback(
    (c, status) => {
      const data = { status };
      if (status === "Accepted" && c.requirementType === "Document") {
        data.docStipStatus = "Accepted";
      }
      patchCondition(c, data);
    },
    [patchCondition],
  );

  /* ── Multi-select / bulk status change ── */
  const exitSelectMode = useCallback(() => {
    setSelectMode(false);
    setSelectedIds(new Set());
    setBulkStatus("");
    setBulkGate("");
  }, []);

  const toggleSelectId = useCallback((id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  // There's no bulk endpoint for conditions, so fire one update per selected
  // item (sequentially) and apply the final response — same pattern the docs
  // tab uses for actions without a bulk endpoint. Applies whichever of the
  // status / type (conditionGate) selectors the user set.
  const applyBulk = useCallback(async () => {
    if ((!bulkStatus && !bulkGate) || selectedIds.size === 0 || bulkSaving)
      return;
    setBulkSaving(true);
    // Skip selected rows that already match every field being set — no need to POST.
    const ids = [...selectedIds].filter((id) => {
      const cond = items.find((c) => c._id === id);
      if (!cond) return false;
      const statusChanges = bulkStatus && cond.status !== bulkStatus;
      const gateChanges = bulkGate && cond.conditionGate !== bulkGate;
      return statusChanges || gateChanges;
    });
    let last = null;
    for (const id of ids) {
      const cond = items.find((c) => c._id === id);
      if (!cond) continue;
      const data = {};
      if (bulkStatus) {
        data.status = bulkStatus;
        if (bulkStatus === "Accepted" && cond.requirementType === "Document") {
          data.docStipStatus = "Accepted";
        }
      }
      if (bulkGate) data.conditionGate = bulkGate;
      // eslint-disable-next-line no-await-in-loop
      const res = await new Promise((resolve) => {
        updateLoanUnderwritingRequirement({
          dealId: loan?._id,
          requirementId: id,
          data,
          onSuccessMsg: null,
          onSuccessFn: (r) => resolve(r),
          onFailFn: () => resolve(null),
          onErrorFn: () => resolve(null),
        });
      });
      if (res) last = res;
    }
    if (last) applyResponse(last);
    setBulkSaving(false);
    exitSelectMode();
  }, [
    bulkStatus,
    bulkGate,
    selectedIds,
    bulkSaving,
    items,
    loan?._id,
    updateLoanUnderwritingRequirement,
    applyResponse,
    exitSelectMode,
  ]);

  // Conditions matching the search box only — the base both filter rows count
  // against (Type is the top-level filter, so its counts ignore Type + Status).
  const searchedItems = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((i) => {
      const text = (
        conditionTitle(i) +
        " " +
        htmlStringToText(i.htmlString || "")
      ).toLowerCase();
      return text.includes(q);
    });
  }, [items, query]);

  // Conditions after the Type (conditionGate) filter — drives the status row so
  // the status counts reflect only the rows the chosen Type displays.
  const typedItems = useMemo(
    () =>
      typeFilter
        ? searchedItems.filter((i) => i.conditionGate === typeFilter)
        : searchedItems,
    [searchedItems, typeFilter],
  );

  // Single flat list of the conditions that pass Type + Status + search, in
  // their natural order (documents, stips and conditions all intermixed).
  const filteredRows = useMemo(
    () =>
      typedItems.filter((i) => {
        if (statusFilter === "needs") return !isAccepted(i);
        if (statusFilter) return (i.status || "Open") === statusFilter;
        return true;
      }),
    [typedItems, statusFilter],
  );

  // Per-status counts for the status row — computed against the type-filtered
  // rows so the numbers track what's actually displayed.
  const statusCounts = useMemo(() => {
    const counts = {};
    typedItems.forEach((i) => {
      const s = i.status || "Open";
      counts[s] = (counts[s] || 0) + 1;
    });
    return counts;
  }, [typedItems]);

  // Per-type (conditionGate) counts for the type filter row (search-scoped).
  const typeCounts = useMemo(() => {
    const counts = {};
    searchedItems.forEach((i) => {
      if (!i.conditionGate) return;
      counts[i.conditionGate] = (counts[i.conditionGate] || 0) + 1;
    });
    return counts;
  }, [searchedItems]);

  // Currently-visible (filtered) rows — drives "select all" and the "displayed"
  // email scope.
  const visibleItems = filteredRows;
  const visibleIds = useMemo(
    () => visibleItems.map((r) => r._id),
    [visibleItems],
  );
  // Source conditions for the email, per the chosen scope.
  const emailSourceItems = useMemo(
    () =>
      emailScope === "displayed"
        ? visibleItems
        : items.filter((c) => emailStatuses.has(c.status || "Open")),
    [emailScope, visibleItems, items, emailStatuses],
  );

  // Copy the conditions list to the clipboard as titled, numbered sections
  // (rich HTML + plain text) so it can be pasted straight into an email.
  const handleCopyEmail = useCallback(async () => {
    if (isEmpty(emailSourceItems)) return;
    const ok = await copyEmailToClipboard(
      buildConditionsEmail(emailSourceItems, loan),
    );
    if (ok) setCopied(true);
  }, [emailSourceItems, loan]);

  // Open the conditions email via the chosen client (Outlook web / OS default).
  const handleOpenEmail = useCallback(
    (opener) => {
      setEmailAnchor(null);
      if (isEmpty(emailSourceItems)) return;
      opener(buildConditionsEmail(emailSourceItems, loan));
    },
    [emailSourceItems, loan],
  );
  const allVisibleSelected =
    visibleIds.length > 0 && visibleIds.every((id) => selectedIds.has(id));
  const toggleSelectAll = useCallback(() => {
    setSelectedIds((prev) => {
      const everySelected =
        visibleIds.length > 0 && visibleIds.every((id) => prev.has(id));
      return everySelected ? new Set() : new Set(visibleIds);
    });
  }, [visibleIds]);

  // Classic view — the table-based redesign tab, with the same view toggle on top.
  if (view === "classic") {
    return (
      <Box sx={{ background: S[50], minHeight: "100%" }}>
        <LoanDrilldownConditionsTab
          headerEndContent={<ViewToggle value={view} onChange={onChangeView} />}
        />
      </Box>
    );
  }

  const total = items.length;

  return (
    <Box sx={{ background: S[50], minHeight: "100%" }}>
      <style>{GLOBAL_CSS}</style>
      <Box
        sx={{
          mx: "auto",
          // maxWidth: embedded ? "none" : 1180,
          // px: embedded ? 2 : 3,
          // pt: embedded ? 2.5 : 4,
          // pb: embedded ? 6 : 12,
          mt: embedded ? 0 : 4,
          // mb: embedded ? 9 : 12,

          width: "100%",
          maxWidth: 1180,
          // maxWidth: 980,
          background: "#fff",
          border: "1px solid #e5e8ee",
          borderRadius: embedded ? 0 : "14px",
          boxShadow:
            "0 1px 2px rgba(15, 23, 42, 0.04), 0 8px 24px -12px rgba(15, 23, 42, 0.08)",
          padding: "22px 24px 10px",
        }}
      >
        <Flx jb wrap g={1.5} sx={{ alignItems: "flex-end" }}>
          <Flx ac g={1.5}>
            {!embedded && (
              <>
                <Txt
                  sx={{
                    fontSize: 24,
                    fontWeight: 700,
                    letterSpacing: "-0.02em",
                    color: S[900],
                  }}
                >
                  Conditions
                </Txt>
                <Flx ac>
                  <Txt sx={{ fontSize: 14, fontWeight: 500, color: S[500] }}>
                    <Box
                      component="span"
                      sx={{ fontWeight: 700, color: S[900] }}
                    >
                      {done}
                    </Box>{" "}
                    of {total} cleared
                  </Txt>
                </Flx>

                {loading && (
                  <CircularProgress size={14} sx={{ color: ACCENT }} />
                )}
                <Tooltip title="Refresh conditions" placement="top">
                  <IconButton
                    size="small"
                    onClick={() =>
                      dispatch(loanFetchedDataUwRequirementsSet(null))
                    }
                  >
                    <SyncOutlined sx={{ fontSize: 17 }} />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </Flx>
          <Flx ac g={0.75}>
            <Tooltip title="Switch to the table view" placement="top">
              <span>
                <Button
                  size="small"
                  variant="ghost"
                  startIcon={<TableChartOutlined />}
                  onClick={() => onChangeView("classic")}
                >
                  Table View
                </Button>
              </span>
            </Tooltip>
            <Tooltip
              title="Select multiple conditions to change their status at once"
              placement="top"
            >
              <span>
                <Button
                  size="small"
                  variant={selectMode ? "solid" : "ghost"}
                  // variant={selectMode ? "contained" : "outlined"}
                  startIcon={<ChecklistRtlOutlined />}
                  onClick={() =>
                    selectMode ? exitSelectMode() : setSelectMode(true)
                  }
                  disabled={isEmpty(items)}
                >
                  {selectMode ? "Done" : "Select"}
                </Button>
              </span>
            </Tooltip>
            <Tooltip
              title="Copy or open the conditions list as an email"
              placement="top"
            >
              <span>
                <Button
                  size="small"
                  variant="ghost"
                  startIcon={copied ? <CheckRounded /> : <EmailOutlined />}
                  endIcon={<KeyboardArrowDownRounded />}
                  onClick={(e) => setEmailAnchor(e.currentTarget)}
                  disabled={isEmpty(items)}
                >
                  {copied ? "Copied" : "Email conditions"}
                </Button>
              </span>
            </Tooltip>
            <Menu
              anchorEl={emailAnchor}
              open={Boolean(emailAnchor)}
              onClose={() => setEmailAnchor(null)}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              slotProps={{ paper: { sx: { minWidth: 288 } } }}
            >
              {/* scope toggle — which conditions go into the email */}
              <Box sx={{ px: 1.25, pt: 1, pb: 0.5 }}>
                <Txt
                  sx={{
                    fontSize: 10,
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                    color: S[400],
                    mb: 0.75,
                  }}
                >
                  Include
                </Txt>
                <Box
                  sx={{
                    display: "flex",
                    background: "#eef3f5",
                    border: "1px solid #e3e9ec",
                    borderRadius: "9px",
                    p: "3px",
                  }}
                >
                  {[
                    { key: "byStatus", label: "By status" },
                    { key: "displayed", label: "Displayed list items" },
                  ].map(({ key, label }) => {
                    const on = emailScope === key;
                    return (
                      <Box
                        key={key}
                        component="button"
                        type="button"
                        onClick={() => setEmailScope(key)}
                        sx={{
                          flex: 1,
                          font: "inherit",
                          fontSize: 11.5,
                          fontWeight: on ? 600 : 500,
                          color: on ? "#0f172a" : S[500],
                          border: "none",
                          background: on ? "#fff" : "transparent",
                          boxShadow: on
                            ? "0 1px 3px rgba(22,36,43,.12)"
                            : "none",
                          px: "10px",
                          py: "6px",
                          borderRadius: "7px",
                          cursor: "pointer",
                          lineHeight: 1.25,
                          textAlign: "center",
                        }}
                      >
                        {label}
                      </Box>
                    );
                  })}
                </Box>

                {/* By status → a stacked multi-checkbox of every status. */}
                {emailScope === "byStatus" && (
                  <Box sx={{ mt: 1, pl: 0.5 }}>
                    {STATUS_OPTIONS.map((s) => {
                      const meta = STATUS[s] || {};
                      const checked = emailStatuses.has(s);
                      return (
                        <Box
                          component="label"
                          key={s}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            pl: 0.5,
                            pr: 1,
                            py: 0.25,
                            borderRadius: "7px",
                            cursor: "pointer",
                            userSelect: "none",
                            transition: "background 0.12s",
                            "&:hover": { background: S[50] },
                          }}
                        >
                          <Checkbox
                            checked={checked}
                            onChange={() => toggleEmailStatus(s)}
                            size="small"
                            disableRipple
                            sx={{
                              p: 0.25,
                              color: S[300],
                              "&.Mui-checked": { color: ACCENT },
                            }}
                          />
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: "50%",
                              background: meta.fg || S[400],
                              flexShrink: 0,
                            }}
                          />
                          <Txt
                            sx={{
                              fontSize: 12.5,
                              fontWeight: checked ? 600 : 500,
                              color: checked ? S[800] : S[600],
                            }}
                          >
                            {meta.label || s}
                          </Txt>
                        </Box>
                      );
                    })}
                  </Box>
                )}
              </Box>
              <Divider sx={{ my: 0.5 }} />
              <MenuItem
                onClick={() => {
                  setEmailAnchor(null);
                  handleCopyEmail();
                }}
                sx={{ fontSize: 13, gap: 1 }}
              >
                <ContentCopyRounded sx={{ fontSize: 16 }} />
                Copy formatted list
              </MenuItem>
              <MenuItem
                onClick={() => handleOpenEmail(openOutlookWebEmail)}
                sx={{ fontSize: 13, gap: 1 }}
              >
                <EmailOutlined sx={{ fontSize: 16 }} />
                Open in Outlook (web)
              </MenuItem>
              <MenuItem
                onClick={() => handleOpenEmail(openDefaultEmailClient)}
                sx={{ fontSize: 13, gap: 1 }}
              >
                <EmailOutlined sx={{ fontSize: 16 }} />
                Open in default email app
              </MenuItem>
            </Menu>
            <Button
              size="small"
              variant="solid"
              startIcon={<AddRounded />}
              onClick={onAdd}
            >
              Add condition
            </Button>
          </Flx>
        </Flx>

        {/* segmented progress — one cell per condition, colored by status */}
        {total > 0 ? (
          <Flx g={0.375} sx={{ mt: 1.5 }}>
            {items.map((i) => (
              <Tooltip
                key={i._id}
                title={`${conditionTitle(i)} — ${STATUS[i.status]?.label || i.status || "Open"}`}
                placement="top"
              >
                <Box
                  onClick={() => onManage(i)}
                  sx={{
                    flex: 1,
                    height: 7,
                    boxSizing: "border-box",
                    borderRadius: 999,
                    cursor: "pointer",
                    background: progressCellColor(i.status),
                    transition: "background 0.25s, transform 0.1s",
                    "&:hover": { transform: "scaleY(1.6)" },
                  }}
                />
              </Tooltip>
            ))}
          </Flx>
        ) : (
          <Box
            sx={{
              mt: 1.5,
              height: 7,
              width: "100%",
              borderRadius: 999,
              background: S[200],
            }}
          />
        )}

        {/* toolbar */}
        <Flx ac g={1.5} sx={{ mt: 2.5 }}>
          <Flx
            ac
            sx={{
              flexGrow: 1,
              borderRadius: 1.5,
              border: `1px solid ${S[200]}`,
              background: "#ffffff",
              px: 1.25,
              "&:focus-within": { boxShadow: `0 0 0 2px ${ACCENT}33` },
            }}
          >
            <SearchRounded sx={{ fontSize: 16, color: S[400] }} />
            <InputBase
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search conditions…"
              sx={{ ml: 1, flex: 1, fontSize: 13, py: 0.5 }}
            />
          </Flx>
        </Flx>

        {/* filter chips */}
        {!isEmpty(items) && (
          <>
            {Object.keys(typeCounts).length > 0 && (
              <Flx ac wrap g={1} sx={{ mt: 2, px: 0.25 }}>
                <TypeFilterTabs
                  value={typeFilter}
                  onChange={setTypeFilter}
                  counts={typeCounts}
                  total={searchedItems.length}
                />
              </Flx>
            )}
            <Flx ac wrap g={1} sx={{ mt: 2, px: 0.25 }}>
              <StatusFilterTabs
                value={statusFilter}
                onChange={setStatusFilter}
                counts={statusCounts}
                total={typedItems.length}
              />
            </Flx>
            {/* <Divider sx={{ mx: -2.5, borderColor: S[200], mt: 2 }} /> */}
          </>
        )}

        {/* bulk action bar */}
        {selectMode && (
          <Flx
            ac
            g={1.5}
            wrap
            sx={{
              mt: 1.5,
              px: 1.5,
              py: 1,
              borderRadius: 1.5,
              border: `1px solid ${ACCENT}3a`,
              background: `${ACCENT}0f`,
            }}
          >
            <Txt sx={{ fontSize: 13, fontWeight: 600, color: S[700] }}>
              {selectedIds.size} selected
            </Txt>
            <Box
              component="button"
              onClick={toggleSelectAll}
              sx={{
                font: "inherit",
                fontSize: 12,
                fontWeight: 500,
                color: ACCENT,
                background: "transparent",
                border: "none",
                cursor: "pointer",
                p: 0,
                "&:hover": { textDecoration: "underline" },
              }}
            >
              {allVisibleSelected ? "Clear all" : "Select all"}
            </Box>
            <Box sx={{ flexGrow: 1 }} />
            <Box
              component="select"
              value={bulkGate}
              onChange={(e) => setBulkGate(e.target.value)}
              sx={SELECT_SX}
            >
              <option value="">Set type…</option>
              {CONDITION_GATE_VALUES.map((g) => (
                <option key={g} value={g}>
                  {getConditionGateLabel(g)}
                </option>
              ))}
            </Box>
            <Box
              component="select"
              value={bulkStatus}
              onChange={(e) => setBulkStatus(e.target.value)}
              sx={SELECT_SX}
            >
              <option value="">Set status…</option>
              {STATUS_OPTIONS.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </Box>
            <Button
              size="small"
              variant="contained"
              onClick={applyBulk}
              disabled={
                (!bulkStatus && !bulkGate) ||
                selectedIds.size === 0 ||
                bulkSaving
              }
              startIcon={
                bulkSaving ? (
                  <CircularProgress size={14} sx={{ color: "inherit" }} />
                ) : (
                  <CheckRounded />
                )
              }
            >
              {bulkSaving ? "Applying…" : "Apply"}
            </Button>
            <Button
              size="small"
              variant="text"
              color="inherit"
              onClick={exitSelectMode}
              disabled={bulkSaving}
            >
              Cancel
            </Button>
          </Flx>
        )}

        {/* AI-suggested conditions — admin-gated (same access as the AI Review
            tab). Reuses the findings queue scoped to condition suggestions;
            promoting one files it as a real condition, then the checklist below
            refetches. Renders nothing when there are no suggestions. */}
        {isAdmin && !aiCondIgnored && (
          <AiFindingsPanel
            scope="conditions"
            onIgnore={ignoreAiConditions}
            ignoreTooltip="Hide AI suggested conditions on every loan. Bring it back from AI Review → Suggested Conditions."
            warningTint
            hideWhenEmpty
            hideIcon
            adminChip
            rootSx={{ mt: 2 }}
            defaultExpanded={false}
            title="AI Suggested Conditions"
            subtitle="Conditions the AI review staged from this loan's documents — not yet on the loan. Promote to add one, edit and promote, or reject."
            onActioned={() =>
              dispatch(loanFetchedDataUwRequirementsSet(null))
            }
          />
        )}

        {/* empty placeholder — no conditions, or none match the filter/search */}
        {filteredRows.length === 0 && !creating && !adding && (
          <Flx
            column
            ac
            g={1}
            sx={{
              // mt: 3,
              my: 3,
              py: 6,
              px: 3,
              textAlign: "center",
              borderRadius: 2,
              border: `1px dashed ${S[200]}`,
              background: "#fff",
            }}
          >
            <ChecklistRtlOutlined sx={{ fontSize: 30, color: S[300] }} />
            <Txt sx={{ fontSize: 14, fontWeight: 600, color: S[700] }}>
              {isEmpty(items)
                ? "No conditions yet"
                : "No conditions match this view"}
            </Txt>
            <Txt sx={{ fontSize: 12.5, color: S[400], maxWidth: 340 }}>
              {isEmpty(items)
                ? "Add the first condition, stip, or missing document requirement for this loan."
                : query
                  ? "Try a different search term, or clear the filters."
                  : "Nothing here for the selected status. Switch to “All” to see everything."}
            </Txt>
            {isEmpty(items) ? (
              <Button
                size="small"
                variant="solid"
                startIcon={<AddRounded />}
                onClick={onAdd}
                sx={{ mt: 1 }}
              >
                Add condition
              </Button>
            ) : (
              (query || statusFilter || typeFilter) && (
                <Button
                  size="small"
                  variant="ghost"
                  onClick={() => {
                    setQuery("");
                    setStatusFilter("");
                    setTypeFilter("");
                  }}
                  sx={{ mt: 1 }}
                >
                  Clear filters
                </Button>
              )
            )}
          </Flx>
        )}

        {/* checklist — a single flat list of all conditions */}
        {(filteredRows.length > 0 || creating || adding) && (
          <Box sx={{ my: 3 }}>
            {filteredRows.map((i, idx) => (
              <ConditionRow
                key={i._id}
                item={i}
                isFirst={idx === 0}
                isOpen={openId === i._id}
                pending={!!pendingIds[i._id]}
                selectMode={selectMode}
                selected={selectedIds.has(i._id)}
                onToggleSelect={() => toggleSelectId(i._id)}
                onToggleOpen={() => setOpenId(openId === i._id ? null : i._id)}
                onToggleDone={() => toggleDone(i)}
                onCycleStatus={() => cycleStatus(i)}
                onPatch={(data) => patchCondition(i, data)}
                onSetStatus={(s) => setStatus(i, s)}
                onManage={() => onManage(i)}
                onDelete={() => handleDelete(i)}
              />
            ))}

            {/* ghost row while a create round-trips */}
            {creating && (
              <Flx
                ac
                g={1.5}
                sx={{
                  px: 2,
                  py: 1.5,
                  borderTop:
                    filteredRows.length === 0 ? "none" : `1px solid ${S[100]}`,
                  opacity: 0.65,
                }}
              >
                <CircularProgress
                  size={16}
                  sx={{ color: ACCENT, flexShrink: 0 }}
                />
                <Txt sx={{ fontSize: 14, fontWeight: 500, color: S[500] }}>
                  {creating.title}
                </Txt>
                <Txt sx={{ ml: "auto", fontSize: 11, color: S[400] }}>
                  Adding…
                </Txt>
              </Flx>
            )}

            {/* add row */}
            <AddRow
              active={adding}
              isFirst={filteredRows.length === 0 && !creating}
              draft={draftTitle}
              onActivate={() => {
                setAdding(true);
                setDraftTitle("");
              }}
              onChange={setDraftTitle}
              onCommit={addItem}
              onCancel={() => {
                setAdding(false);
                setDraftTitle("");
              }}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

/* ----------------------------------- row ----------------------------------- */

const ConditionRow = ({
  item,
  isFirst,
  isOpen,
  pending,
  selectMode,
  selected,
  onToggleSelect,
  onToggleOpen,
  onToggleDone,
  onCycleStatus,
  onPatch,
  onSetStatus,
  onManage,
  onDelete,
}) => {
  const done = isAccepted(item);
  const title = conditionTitle(item);
  const due = dueLabel(item.dueDateTime || item.dueDate, done);
  const docCount = size(conditionDocIds(item));
  const commentCount = size(item.comments || item.conditionComments);
  // Document requirements always surface their upload count (even 0 → "Missing").
  const isDocType = item.requirementType === "Document" || !isNil(item.docType);

  return (
    <Box
      sx={{
        position: "relative",
        borderTop: isFirst ? "none" : `1px solid ${S[100]}`,
      }}
    >
      {/* accent bar spans the row AND the expanded tray, without shifting content */}
      {isOpen && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            bottom: 0,
            left: 0,
            width: "3px",
            background: ACCENT_BAR,
            zIndex: 1,
          }}
        />
      )}

      <Flx
        // ac
        g={1.5}
        sx={{
          px: 2,
          py: 1.5,
          transition: "background 0.15s",
          background: selected
            ? `${ACCENT}14`
            : isOpen
              ? "rgba(236,254,255,0.8)"
              : "transparent",
          "&:hover": {
            background: selected
              ? `${ACCENT}1f`
              : isOpen
                ? "rgba(236,254,255,0.8)"
                : S[50],
          },
        }}
      >
        {/* selection checkbox (bulk mode only) */}
        {selectMode && (
          <Box
            component="input"
            type="checkbox"
            checked={selected}
            onChange={onToggleSelect}
            sx={{
              flexShrink: 0,
              width: 16,
              height: 16,
              mt: "2px",
              accentColor: ACCENT,
              cursor: "pointer",
            }}
          />
        )}

        {/* accept check — hidden in select mode (the selection checkbox takes
            its place); swaps to a spinner while the row is saving */}
        {!selectMode &&
          (pending ? (
            <Flx
              ac
              sx={{
                flexShrink: 0,
                height: 20,
                width: 20,
                justifyContent: "center",
              }}
            >
              <CircularProgress size={15} sx={{ color: ACCENT }} />
            </Flx>
          ) : (
            <Tooltip
              title={done ? "Reopen (mark not accepted)" : "Mark accepted"}
              placement="top"
            >
              <Box
                component="button"
                onClick={onToggleDone}
                aria-label={done ? "Reopen" : "Mark accepted"}
                sx={{
                  flexShrink: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: 20,
                  width: 20,
                  borderRadius: "50%",
                  cursor: "pointer",
                  border: `2px solid ${done ? "#10b981" : S[300]}`,
                  background: done ? "#10b981" : "transparent",
                  color: done ? "#fff" : "transparent",
                  transition: "all 0.15s",
                  "&:hover": {
                    borderColor: "#34d399",
                    color: done ? "#fff" : "#34d399",
                  },
                }}
              >
                <CheckRounded sx={{ fontSize: 13 }} />
              </Box>
            </Tooltip>
          ))}

        {/* title — toggles the inline master-detail (in select mode it toggles
            the row's selection instead) */}
        <Box
          component="button"
          onClick={selectMode ? onToggleSelect : onToggleOpen}
          sx={{
            minWidth: 0,
            flex: 1,
            display: "flex",
            alignItems: "center",
            gap: 1.25,
            border: "none",
            background: "transparent",
            cursor: "pointer",
            textAlign: "left",
            p: 0,
            opacity: pending ? 0.6 : 1,
            transition: "opacity 0.15s",
          }}
        >
          <Box
            component="span"
            sx={{
              minWidth: 0,
              // overflow: "hidden",
              // textOverflow: "ellipsis",
              // whiteSpace: "nowrap",
              fontSize: 14,
              fontWeight: 500,
              lineHeight: 1.5,
              color: done ? S[400] : S[800],
              textDecoration: done ? "line-through" : "none",
              textDecorationColor: done ? S[300] : "inherit",
            }}
          >
            {title}
          </Box>
          {item.autoGenerated && (
            <Tooltip title="System generated" placement="top">
              <BoltRounded
                sx={{ flexShrink: 0, fontSize: 14, color: S[300] }}
              />
            </Tooltip>
          )}
          {item.displayExternally && (
            <Tooltip title="Visible to borrower" placement="top">
              <VisibilityOutlined
                sx={{ flexShrink: 0, fontSize: 14, color: `${ACCENT}b3` }}
              />
            </Tooltip>
          )}
        </Box>

        {/* right indicators */}
        <Flx
          ac
          g={1.25}
          sx={{
            flexShrink: 0,
            color: S[400],
            opacity: pending ? 0.6 : 1,
            transition: "opacity 0.15s",
          }}
        >
          {isDocType ? (
            <Tooltip
              title={
                docCount > 0
                  ? `${docCount} document${docCount === 1 ? "" : "s"} uploaded`
                  : "No document uploaded yet"
              }
              placement="top"
            >
              <Flx
                ac
                g={0.25}
                sx={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: docCount > 0 ? S[400] : "#b45309",
                }}
              >
                <AttachFileOutlined sx={{ fontSize: 13 }} />
                {docCount > 0 ? docCount : "Missing"}
              </Flx>
            </Tooltip>
          ) : (
            docCount > 0 && (
              <Flx ac g={0.25} sx={{ fontSize: 11, fontWeight: 500 }}>
                <AttachFileOutlined sx={{ fontSize: 13 }} />
                {docCount}
              </Flx>
            )
          )}
          {commentCount > 0 && (
            <Flx ac g={0.25} sx={{ fontSize: 11, fontWeight: 500 }}>
              <ChatBubbleOutlineRounded sx={{ fontSize: 13 }} />
              {commentCount}
            </Flx>
          )}
          {due && !done && (
            <Flx
              ac
              g={0.5}
              sx={{
                fontSize: 11,
                color: due.color,
                fontWeight: due.bold ? 600 : 400,
              }}
            >
              <EventOutlined sx={{ fontSize: 12 }} />
              {due.label}
            </Flx>
          )}
          <GateChip gate={item.conditionGate} />
          {!done && (
            <StatusChip
              status={item.status}
              onClick={onCycleStatus}
              loading={pending}
            />
          )}
          <KeyboardArrowDownRounded
            onClick={onToggleOpen}
            sx={{
              cursor: "pointer",
              fontSize: 18,
              color: S[300],
              transition: "transform 0.2s",
              transform: isOpen ? "rotate(180deg)" : "none",
            }}
          />
        </Flx>
      </Flx>

      {isOpen && (
        <Box sx={{ borderBottom: "2px solid #cffafe" }}>
          <RowDetail
            item={item}
            pending={pending}
            onPatch={onPatch}
            onSetStatus={onSetStatus}
            onManage={onManage}
            onDelete={onDelete}
          />
        </Box>
      )}
    </Box>
  );
};

/* ------------------------------ expanded detail ---------------------------- */

export const RowDetail = ({
  item,
  pending,
  onPatch,
  onSetStatus,
  onManage,
  onDelete,
}) => {
  const loanId = useSelector((state) => state.loanDrilldown?._id);
  const loanDocuments = useSelector(
    (state) => state.loanDrilldown?.loanDocuments,
  );
  const users = useSelector((state) => state.users);
  const comments = item.comments || item.conditionComments;
  const commentCount = size(comments);
  const docIds = conditionDocIds(item);
  const docCount = size(docIds);
  const title = conditionTitle(item);
  const detailsText = htmlStringToText(item.htmlString || "");
  // Only surface the body when it adds something beyond the row title —
  // repeating the title verbatim wastes the most prominent slot in the panel.
  const hasDetails =
    detailsText.length > 0 && detailsText.trim() !== title.trim();
  const isDocument =
    item.requirementType === "Document" || !isNil(item.docStipStatus);
  const dueValue = item.dueDate || item.dueDateTime;
  const resolvedName = item.resolvedBy
    ? getUserFullNameFromEmail(users, item.resolvedBy, "Unknown")
    : null;

  return (
    <Box
      className="ckPop"
      sx={{
        borderTop: `1px solid ${S[200]}`,
        // background: "rgba(241,245,249,0.9)", // slate-100/90
        background: "#fbfcfd",
        boxShadow: "inset 0 2px 4px rgba(0,0,0,0.05)",
        px: 2.5,
        pt: 3,
        pb: 6,

        pl: 6, // aligns content under the row title
      }}
    >
      <Box sx={{ maxWidth: 1400, margin: "0 auto" }}>
        <Flx ac jb wrap g={1.5} sx={{ mb: 2 }}>
          <Flx column g={0}>
            <Flx ac wrap g={1}>
              <Txt sx={{ fontSize: 18, fontWeight: 700, color: S[800] }}>
                {/* Condition Details */}
                {conditionTitle(item)}
              </Txt>
              {item.autoGenerated && (
                <DetailTag
                  icon={<BoltRounded sx={{ fontSize: 12 }} />}
                  label="Auto Generated"
                  fg="#4338ca"
                  bg="#eef2ff"
                  border="#c7d2fe"
                />
              )}
              {item.displayExternally && (
                <DetailTag
                  icon={<VisibilityOutlined sx={{ fontSize: 12 }} />}
                  label="External Facing"
                  fg={ACCENT}
                  bg={`${ACCENT}14`}
                  border={`${ACCENT}55`}
                />
              )}
            </Flx>
            {isDocument && (
              <Flx
                // g={2}
                sx={
                  {
                    // background: S[50],
                    // py: 1,
                    // width: "fit-content",
                    // borderRadius: 1,
                    // flexGrow: 0,
                    // border: `1px solid ${S[200]}`,
                  }
                }
              >
                {item.docType && (
                  <Flx
                    ac
                    g={0.5}
                    sx={{
                      pr: 1,
                      mr: 1,
                      borderRight: `1px solid ${S[200]}`,
                    }}
                  >
                    <Txt sx={{ fontSize: 12, color: S[400] }}>
                      Requires Document Type:
                    </Txt>
                    <Txt
                      sx={{
                        fontSize: 12,
                        fontWeight: 600,
                        color: S[700],
                      }}
                    >
                      {item.docType}
                    </Txt>
                  </Flx>
                )}
                {item.docGroup && (
                  <Flx ac g={0.5}>
                    <Txt sx={{ fontSize: 12, color: S[400] }}>
                      Document Category:
                    </Txt>
                    <Txt
                      sx={{
                        fontSize: 12,
                        fontWeight: 600,
                        color: S[700],
                      }}
                    >
                      {item.docGroup}
                    </Txt>
                  </Flx>
                )}
              </Flx>
            )}
          </Flx>
          <Button
            variant="text"
            size="small"
            color={"secondary"}
            endIcon={<EastOutlined sx={{ fontSize: 14 }} />}
            onClick={onManage}
            sx={{ textTransform: "none", color: "#0891b2", fontWeight: 600 }}
          >
            All Condition Details
          </Button>
        </Flx>

        <Box
          sx={{
            display: "grid",
            gap: 3,
            gridTemplateColumns: { xs: "1fr", lg: "1fr 320px" },
            // gridTemplateColumns: { xs: "1fr", lg: "1fr 260px" },
          }}
        >
          {/* --------------------------- left column --------------------------- */}
          <Flx column g={2}>
            <DetailCard>
              <Flx column g={4}>
                {/* Row 1 — gate on the left, document toggle on the right. Peer
                    LabeledFields keep the labels on one line and the controls
                    on the next. */}
                <Flx wrap g={4} sx={{ alignItems: "flex-start" }}>
                  <LabeledField label="Type">
                    <StatusPills
                      value={item.conditionGate}
                      onChange={(v) => onPatch({ conditionGate: v })}
                      loading={pending}
                      options={CONDITION_GATE_VALUES}
                      statusMeta={CONDITION_GATE_META}
                      fallbackKey={CONDITION_GATE.PRIOR_TO_DOCS}
                    />
                  </LabeledField>
                  <LabeledField label="Requires Document">
                    <YesNoSwitch
                      checked={isDocument}
                      disabled={pending}
                      sx={{ px: 0, py: 0 }}
                      onChange={(next) =>
                        onPatch({
                          requirementType: next ? "Document" : "Condition",
                        })
                      }
                    />
                  </LabeledField>
                </Flx>

                {/* Row 2 — condition status, full width. */}
                <LabeledField label="Condition Status">
                  <StatusPills
                    value={item.status || "Open"}
                    onChange={onSetStatus}
                    loading={pending}
                  />
                </LabeledField>

                {/* Row 3 — the document pair, only once this is a doc condition. */}
                {isDocument && (
                  <Flx wrap g={4} sx={{ alignItems: "flex-start" }}>
                    <RequiredDocTypeField
                      item={item}
                      pending={pending}
                      onPatch={onPatch}
                    />
                    <LabeledField label="Required Document Status">
                      <StatusPills
                        value={item.docStipStatus || "Open Item"}
                        onChange={(s) => onPatch({ docStipStatus: s })}
                        loading={pending}
                        options={DOC_STIP_STATUS_OPTIONS}
                        statusMeta={DOC_STATUS}
                        fallbackKey="Open Item"
                      />
                    </LabeledField>
                  </Flx>
                )}
              </Flx>
            </DetailCard>

            {/* documents — type/category, the parallel doc-status selector, an
              upload button (modal), and the list of attached files. (Linking
              *existing* loan files is done through Associations below.) */}
            <DetailCard>
              <DetailSection
                title="Condition Documents"
                count={docCount}
                endContent={
                  <ConditionFileUpload loanId={loanId} condition={item} />
                }
              >
                {/* doc type / category */}

                <FlxGrid grow={1} gap={4} sx={{ pt: 2 }}>
                  {/* document status — parallel doc lifecycle, only for documents */}
                  {/* document status — parallel doc lifecycle, only for documents */}

                  <Box>
                    <Box
                      sx={{
                        //
                        ...SUB_SECTION_LABEL_SX,
                      }}
                    >
                      Uploaded Documents
                    </Box>
                    {docCount > 0 ? (
                      <Box sx={{ mt: 1.5 }}>
                        <DocumentIdsRenderer
                          documents={loanDocuments}
                          documentIds={docIds}
                          emptyLabelFontSize={11}
                        />
                      </Box>
                    ) : (
                      <Txt sx={{ fontSize: 12, mt: 1 }}>
                        No files attached yet.
                      </Txt>
                    )}
                  </Box>

                  {/* {isDocument && (
                    <Box>
                      <Box
                        sx={{
                          //
                          ...SUB_SECTION_LABEL_SX,
                        }}
                      >
                        Document Status
                      </Box>
                      <StatusPills
                        value={item.docStipStatus || "Open Item"}
                        onChange={(s) => onPatch({ docStipStatus: s })}
                        loading={pending}
                        options={DOC_STIP_STATUS_OPTIONS}
                        statusMeta={DOC_STATUS}
                        fallbackKey="Open Item"
                      />
                    </Box>
                  )} */}
                </FlxGrid>
                {/* {isDocument && (
                  <Box>
                    <Box
                      sx={{
                        //
                        mt: 4,
                        ...SUB_SECTION_LABEL_SX,
                      }}
                    >
                      Required Document Type
                    </Box>
                    {(item.docType || item.docGroup) && (
                      <Flx
                        // g={1}
                        sx={{
                          background: S[50],
                          // px: 1.2,
                          py: 1,

                          width: "fit-content",
                          borderRadius: 1,
                          flexGrow: 0,
                          border: `1px solid ${S[200]}`,
                        }}
                      >
                        {item.docGroup && (
                          <Flx
                            ac
                            g={0.5}
                            sx={{
                              //
                              px: 1.5,
                              // py: 0.5,
                              // py: 1,
                              // px: 1,
                            }}
                          >
                            <Txt sx={{ fontSize: 12, color: S[400] }}>
                              Document Category:
                            </Txt>
                            <Txt
                              sx={{
                                fontSize: 12,
                                fontWeight: 600,
                                color: S[700],
                              }}
                            >
                              {item.docGroup}
                            </Txt>
                          </Flx>
                        )}
                        {item.docType && (
                          <Flx
                            ac
                            g={0.5}
                            sx={{
                              borderLeft: `1px solid ${S[200]}`,
                              px: 1.5,
                              // ml: 2.5,
                              // py: 0.5,
                              // py: 1,
                              // px: 1,
                            }}
                          >
                            <Txt sx={{ fontSize: 12, color: S[400] }}>
                              Document Type:
                            </Txt>
                            <Txt
                              sx={{
                                fontSize: 12,
                                fontWeight: 600,
                                color: S[700],
                              }}
                            >
                              {item.docType}
                            </Txt>
                          </Flx>
                        )}
                      </Flx>
                    )}
                  </Box>
                )} */}
                {/* upload (button → modal) + existing files */}
              </DetailSection>
            </DetailCard>

            {/* Details */}
            {item?.htmlString && (
              <DetailCard sx={{ p: 0, mb: 0 }}>
                <DetailSection
                  title="Description"
                  labelSx={{ px: 2, pt: 1.5, mb: 0.5 }}
                >
                  <Box sx={{ px: 0.6 }}>
                    <HtmlEditor
                      value={item?.htmlString} // Pass initial value only
                      suppressOuterBorder
                      readOnly
                    />
                  </Box>
                </DetailSection>
              </DetailCard>
              // <Box
              //   sx={{
              //     mb: 2,
              //     maxWidth: 680,
              //     fontSize: 13,
              //     lineHeight: 1.6,
              //     color: S[600],
              //   }}
              // >
              //   <HtmlEditor value={item.htmlString} suppressOuterBorder readOnly />
              // </Box>
            )}

            {/* activity */}
            <DetailCard>
              <DetailSection
                title="Activity"
                icon={<ChatBubbleOutlineRounded sx={{ fontSize: 11 }} />}
                count={commentCount}
              >
                {!isEmpty(comments) && (
                  <Box sx={{ mb: 1.5 }}>
                    <CommentsRenderer
                      onlyComments
                      hideHeader
                      hideIfEmpty
                      commentThreadTopMargin={0}
                      comments={comments}
                    />
                  </Box>
                )}
                <LoanConditionCommentInput
                  loanId={loanId}
                  conditionId={item._id}
                  commentThreadTopMargin={0.1}
                  inputTypeBasic
                />
              </DetailSection>
            </DetailCard>
          </Flx>

          {/* --------------------------- right column -------------------------- */}
          <Flx column g={2}>
            {/* properties */}
            <DetailCard>
              <DetailSection
                title="Details"
                icon={<AttachFileOutlined sx={{ fontSize: 11 }} />}
                endContent={
                  <DetailLink
                    accent
                    onClick={onManage}
                    icon={<EditOutlined sx={{ fontSize: 10 }} />}
                  >
                    Edit
                  </DetailLink>
                }
              >
                <Flx column g={0} sx={{ fontSize: 13 }}>
                  <DetailRow
                    label="Type"
                    value={
                      getConditionGateLabel(item.conditionGate) || "—"
                    }
                  />
                  <DetailRow
                    label="Required Document"
                    value={
                      (isDocument && (item.docType || item.docGroup)) || "None"
                    }
                  />
                  {dueValue && (
                    <DetailRow label="Due" value={formatDate(dueValue)} />
                  )}
                  <Flx
                    ac
                    jb
                    g={1.5}
                    sx={{ borderTop: `1px solid ${S[100]}`, pt: 1, mt: 1 }}
                  >
                    <Flx ac g={0.75} sx={{ color: S[400], fontSize: 11 }}>
                      <VisibilityOutlined
                        sx={{ fontSize: 12, color: `${ACCENT}cc` }}
                      />
                      Borrower can see
                    </Flx>
                    <input
                      type="checkbox"
                      checked={!!item.displayExternally}
                      disabled={pending}
                      onChange={(e) =>
                        onPatch({ displayExternally: e.target.checked })
                      }
                      style={{ width: 14, height: 14, accentColor: ACCENT }}
                    />
                  </Flx>
                </Flx>
              </DetailSection>
            </DetailCard>

            {/* resolution — only once resolved */}
            {(resolvedName || item.resolutionNotes) && (
              <DetailCard
                sx={{
                  background: "#ecfdf5",
                  border: "1px solid #bae5d8",
                }}
              >
                <DetailSection
                  title="Resolution"
                  icon={<CheckRounded sx={{ fontSize: 11 }} />}
                  labelSx={{ color: "#059669" }}
                >
                  {resolvedName && (
                    <Txt sx={{ fontSize: 12, color: "#065f46" }}>
                      <b style={{ fontWeight: 700 }}>Resolved by:</b>{" "}
                      {resolvedName}
                    </Txt>
                  )}
                  {item.resolutionNotes && (
                    <Txt sx={{ fontSize: 12, color: "#065f46", mt: 0.25 }}>
                      {htmlStringToText(item.resolutionNotes)}
                    </Txt>
                  )}
                </DetailSection>
              </DetailCard>
            )}
            {/* associations — link borrowers / properties / entities / existing loan
          documents to this condition (same editor as the sidebar). */}
            <DetailCard>
              <DetailSection
                title="Loan Associations"
                icon={<LinkOutlined sx={{ fontSize: 11 }} />}
              >
                <ConditionAssociations dealId={loanId} condition={item} />
              </DetailSection>
            </DetailCard>

            {/* metadata */}
            <DetailCard>
              <DetailSection
                title="Metadata"
                icon={<InfoOutlined sx={{ fontSize: 11 }} />}
              >
                <DetailRow
                  label="Created"
                  value={formatDateTime(item?.commentDateTime)}
                />
                <DetailRow label="Created By" value={item?.createdBy} />
                <DetailRow
                  label="Updated"
                  value={formatDateTime(item?.updateDateTime)}
                />
                <DetailRow label="Updated By" value={item?.updatedBy} />
                <DetailRow
                  label="Auto Generated"
                  value={item?.autoGenerated ? "Yes" : "No"}
                />
                <DetailRow
                  label="Display Externally"
                  value={item?.displayExternally ? "Yes" : "No"}
                />
              </DetailSection>
            </DetailCard>
          </Flx>
        </Box>

        {/* footer actions */}
        <Flx
          ac
          sx={{
            mt: 2,
            pt: 1.5,
            borderTop: "1px solid rgba(226,232,240,0.8)",
            justifyContent: "flex-end",
          }}
        >
          <Box
            component="button"
            onClick={onDelete}
            disabled={pending}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.75,
              border: "none",
              background: "transparent",
              cursor: pending ? "default" : "pointer",
              borderRadius: "6px",
              px: 1,
              py: 0.5,
              fontSize: 12,
              fontWeight: 500,
              color: S[400],
              opacity: pending ? 0.5 : 1,
              transition: "all 0.15s",
              "&:hover": pending
                ? {}
                : { background: "#fef2f2", color: "#dc2626" },
            }}
          >
            {pending ? (
              <CircularProgress size={12} sx={{ color: S[400] }} />
            ) : (
              <DeleteOutlineRounded sx={{ fontSize: 14 }} />
            )}
            Delete condition
          </Box>
        </Flx>
      </Box>
      {/* header — title, status tags, and a button into the full editor */}
    </Box>
  );
};

/* ------------------------------ small pieces ------------------------------- */

// Label / value row inside the Details card.
const DetailRow = ({ label, value }) => (
  <Flx ac jb g={1.5} sx={{ borderTop: `1px solid ${S[100]}`, pt: 1, mt: 1 }}>
    <Txt sx={{ fontSize: 11, color: S[400] }}>{label}</Txt>
    <Txt
      sx={{ fontSize: 11, fontWeight: 500, color: S[700], textAlign: "right" }}
    >
      {value}
    </Txt>
  </Flx>
);

// Small rounded status tag shown in the detail header (Auto Generated / External).
const DetailTag = ({ icon, label, fg, bg, border }) => (
  <Flx
    ac
    g={0.4}
    sx={{
      px: 0.85,
      py: 0.3,
      borderRadius: 999,
      fontSize: 11,
      fontWeight: 600,
      color: fg,
      background: bg,
      border: `1px solid ${border}`,
    }}
  >
    {icon}
    {label}
  </Flx>
);

// Low-emphasis link that opens the existing condition sidebar (full editor).
/* ------------------- required document type (read → edit) ------------------ */

// Sits to the right of the "Requires Document" switch. Shows the resolved
// doc type as text with a pencil; the category/type picker only mounts once
// the pencil is clicked, so the row stays compact in its resting state.
const RequiredDocTypeField = ({ item, pending, onPatch }) => {
  const [editing, setEditing] = useState(false);
  const value = item.docType || item.docGroup;

  return (
    <LabeledField label="Required Document Type">
      {editing ? (
        <Flx ac g={1.5}>
          <Box sx={{ minWidth: 260, maxWidth: 380 }}>
            <SelectFileAssignmentInput
              size="small"
              onlyDisplayDocType
              placeholder="Select document category / type…"
              docGroup={item.docGroup}
              docType={item.docType}
              onChange={(params) => {
                onPatch({
                  docGroup: params?.docGroup,
                  docType: params?.docType,
                });
                setEditing(false);
              }}
            />
          </Box>
          <DetailLink onClick={() => setEditing(false)}>Cancel</DetailLink>
        </Flx>
      ) : (
        <Flx ac g={1}>
          <Txt
            sx={{
              fontSize: 13,
              fontWeight: value ? 600 : 400,
              color: value ? S[800] : S[400],
              fontStyle: value ? "normal" : "italic",
            }}
          >
            {value || "Not set"}
          </Txt>
          <Box
            component="button"
            type="button"
            disabled={pending}
            onClick={() => setEditing(true)}
            aria-label="Edit required document type"
            sx={{
              display: "inline-flex",
              alignItems: "center",
              border: "none",
              background: "transparent",
              p: 0,
              cursor: pending ? "default" : "pointer",
              color: S[400],
              "&:hover": { color: ACCENT },
            }}
          >
            <EditOutlined sx={{ fontSize: 13 }} />
          </Box>
        </Flx>
      )}
    </LabeledField>
  );
};

const DetailLink = ({ onClick, icon, accent, children }) => (
  <Box
    component="button"
    onClick={onClick}
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: 0.6,
      border: "none",
      background: "transparent",
      cursor: "pointer",
      fontSize: 10,
      fontWeight: 600,
      color: accent ? ACCENT : S[500],
      p: 0,
      "&:hover": {
        color: ACCENT,
        textDecoration: accent ? "underline" : "none",
      },
    }}
  >
    {icon} {children}
  </Box>
);

/* --------------------- condition file upload (inline) ---------------------- */

// Mirrors the sidebar's UploadFileToLoanConditionProcess, but behind a button +
// modal (no inline drag-and-drop): uploads against the underwriting requirement
// then refreshes both the loan (for loanDocuments) and the uw-requirements slice.
const ConditionFileUpload = ({ loanId, condition }) => {
  const dispatch = useDispatch();
  const { loading, uploadFilesToUnderwritingRequirement } =
    useChecklistRouteHook();
  const { axiosGetIsLoading, axiosPostIsLoading, getLoanById } =
    useUnderwritingHook();
  const [open, setOpen] = useState(false);

  const handleOnSubmit = (files, { onSuccessFn } = {}) => {
    uploadFilesToUnderwritingRequirement({
      dealId: loanId,
      requirementId: condition?._id,
      files,
      onSuccessFn: (d) => {
        getLoanById({
          loanId,
          onSuccessFn: (data) => {
            dispatch(loanDrilldownSet(data));
            dispatch(loanFetchedDataUwRequirementsSet(d));
            onSuccessFn?.();
            setOpen(false);
          },
        });
      },
    });
  };

  return (
    <>
      {/* <Button
        variant="outlined"
        size="small"
        startIcon={<FileUploadOutlined sx={{ fontSize: 16 }} />}
        onClick={() => setOpen(true)}
        sx={{ textTransform: "none", fontWeight: 600 }}
      >
        Upload Document
      </Button> */}
      <DetailLink
        accent
        onClick={() => setOpen(true)}
        icon={<AttachFileOutlined sx={{ fontSize: 12 }} />}
      >
        Upload Document
      </DetailLink>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: 16, fontWeight: 700 }}>
          Upload Document
        </DialogTitle>
        <DialogContent>
          <FileUploadProcess
            loanId={loanId}
            docType={condition?.docType}
            docGroup={condition?.docGroup}
            borderSize={2}
            uploadHeight={"120px"}
            disableCategoryAndTypeSelection
            categoryAndTypeLabel="Select Document Category"
            onSubmit={handleOnSubmit}
            loading={loading || axiosGetIsLoading || axiosPostIsLoading}
          />
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={() => setOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

/* -------------------- condition associations (inline) ---------------------- */

// Mirrors the sidebar's ConditionAssociationsEditor — view chips + an editor
// dialog to link borrowers / properties / entities / existing loan documents
// to the condition. Persists the whole associations map via the uw-requirement
// update endpoint.
const ConditionAssociations = ({ dealId, condition }) => {
  const dispatch = useDispatch();
  const loan = useSelector((state) => state.loanDrilldown);
  const { loading: saving, updateLoanUnderwritingRequirement } =
    useChecklistRouteHook();
  const [editing, setEditing] = useState(false);

  const handleSave = useCallback(
    (newAssociations) => {
      if (!condition?._id) return;
      updateLoanUnderwritingRequirement({
        dealId,
        requirementId: condition._id,
        data: { associations: newAssociations },
        onSuccessMsg: "Associations updated",
        onSuccessFn: (response) => {
          let nextConditions = null;
          if (isArray(response)) nextConditions = response;
          else if (isArray(response?.conditions))
            nextConditions = response.conditions;
          else if (isArray(response?.comments))
            nextConditions = response.comments;
          if (nextConditions) {
            dispatch(loanFetchedDataUwRequirementsSet(nextConditions));
          }
          setEditing(false);
        },
      });
    },
    [dealId, condition?._id, updateLoanUnderwritingRequirement, dispatch],
  );

  if (!condition?._id) return null;

  const hostLabel = condition?.docType
    ? `Condition · ${condition.docType}`
    : "Condition";

  return (
    <>
      <AssociationsDisplay
        loan={loan}
        associations={condition.associations || {}}
        onEdit={() => setEditing(true)}
      />
      <AssociationsEditor
        open={editing}
        loan={loan}
        initial={condition.associations || {}}
        hostLabel={hostLabel}
        onSave={handleSave}
        onClose={() => !saving && setEditing(false)}
        saving={saving}
      />
    </>
  );
};

/* ----------------------------------- add ----------------------------------- */

const AddRow = ({
  active,
  isFirst,
  draft,
  onActivate,
  onChange,
  onCommit,
  onCancel,
}) => {
  if (active) {
    return (
      <Flx
        ac
        g={1.5}
        sx={{
          px: 2,
          py: 1.25,
          borderTop: isFirst ? "none" : `1px solid ${S[100]}`,
        }}
      >
        <Box
          sx={{
            flexShrink: 0,
            height: 20,
            width: 20,
            borderRadius: "50%",
            border: `2px dashed ${S[300]}`,
          }}
        />
        <InputBase
          autoFocus
          value={draft}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") onCommit();
            if (e.key === "Escape") onCancel();
          }}
          onBlur={onCommit}
          placeholder="Type a condition and press Enter…"
          sx={{ flex: 1, fontSize: 14 }}
        />
      </Flx>
    );
  }
  return (
    <Box
      component="button"
      onClick={onActivate}
      sx={{
        width: "100%",
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        border: "none",
        borderTop: isFirst ? "none" : `1px solid ${S[100]}`,
        background: "transparent",
        cursor: "pointer",
        px: 2,
        py: 1.25,
        fontSize: 13,
        fontWeight: 500,
        color: S[400],
        transition: "all 0.15s",
        "&:hover": { background: S[50], color: ACCENT },
      }}
    >
      <AddRounded sx={{ fontSize: 17, ml: 0.25 }} /> Add condition
    </Box>
  );
};

export default LoanDrilldownConditionsChecklistTab;
